// confirm dialog using tailwindcss
import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import Spinner from '../Spinner/Spinner';
import classNames from '@/helpers/classNames';

export interface ConfirmProps {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => boolean | Promise<boolean | undefined>;
  onCancel?: () => void;
  type?: 'danger' | 'warning' | 'info';
}

export const Confirm = ({
  title,
  description,
  onConfirm,
  cancelText = 'Cancel',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onCancel = () => {},
  confirmText = 'Confirm',
  type = 'danger'
}: ConfirmProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);

  return (
    <Transition.Root show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={() => setIsOpen(false)}>
        <div
          className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0"
          data-cy="alert-dialog">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {title}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">{description}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                {onConfirm && (
                  <button
                    type="button"
                    data-cy="confirm-button"
                    className={classNames(
                      'inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm  focus:outline-none focus:ring-2  focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm',
                      {
                        'bg-red-600 hover:bg-red-700 focus:ring-red-500': type === 'danger',
                        'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500':
                          type === 'warning',
                        'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500': type === 'info'
                      }
                    )}
                    onClick={async () => {
                      setConfirmLoading(true);
                      const ok = await onConfirm();
                      setConfirmLoading(false);
                      if (ok) {
                        setIsOpen(false);
                      }
                    }}>
                    {confirmText}
                    {confirmLoading && <Spinner className="ml-2" />}
                  </button>
                )}
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    setIsOpen(false);
                    onCancel();
                  }}>
                  {cancelText}
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
